import React, { useCallback, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/custom-theme-button';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import Popup from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { fontSize } from '@atlassian/jira-common-styles/src';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message';
import { useIntl } from '@atlassian/jira-intl';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import messages from './messages';

export const InfoIcon = () => {
	const [isOpen, setIsOpen] = useState(false);
	const { isSiteAdmin } = useTenantContext();

	const { formatMessage } = useIntl();

	const toggleDialog = useCallback(() => {
		setIsOpen((oldState) => !oldState);
	}, [setIsOpen]);

	const onCloseDialog = useCallback(() => setIsOpen(false), [setIsOpen]);

	const content = () => (
		<DialogContent data-testId="issue-create.ui.modal.create-form.status-field.tooltip">
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>
				{isSiteAdmin ? (
					<FormattedI18nMessage
						componentsMapping={{ bold: BoldFont }}
						message={formatMessage(messages.changeboardingAdmin, {
							boldStart: '{boldStart}',
							boldEnd: '{boldEnd}',
						})}
					/>
				) : (
					formatMessage(messages.changeboarding)
				)}
			</p>
		</DialogContent>
	);

	return (
		<ChangeboardingWrapper>
			<Popup
				isOpen={isOpen}
				onClose={onCloseDialog}
				placement="right"
				zIndex={layers.modal()}
				shouldRenderToParent
				content={content}
				trigger={(triggerProps) => (
					<Button
						data-ds--inline-message--button
						appearance="subtle-link"
						onClick={toggleDialog}
						spacing="none"
						testId="issue-create.ui.modal.create-form.status-field.changeboarding.info-icon"
						{...triggerProps}
					>
						<IconWrapper isOpen={isOpen}>
							<EditorPanelIcon label="info" size="small" />
						</IconWrapper>
					</Button>
				)}
			/>
		</ChangeboardingWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoldFont = styled.span<{ children: ReactNode }>({
	fontWeight: 500,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DialogContent = styled.div({
	fontWeight: 400,
	padding: `${token('space.100', '8px')} ${token('space.200', '16px')}`,
	maxWidth: '300px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span<{ isOpen: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	'-IconAccentColor': token('color.icon.accent.gray', colors.N200),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	'-IconColor': token('color.icon.accent.gray', colors.N100),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: `var(${(props: any) => (props.isOpen ? '--icon-accent-color' : '--icon-color')})`,
	'&:hover': {
		color: 'var(--icon-accent-color)',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChangeboardingWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		margin: 0,
	},
});
